import SearchList, { SearchListWrapper } from '../components/SearchList'
import { TitleButton } from '../components/TitleButton'
import LabelInput from '../components/LabelInput'
import LabelAutoComplete from '../components/LabelAutoComplete'
import LabelOnly from '../components/LabelOnly'
import LabelUnit from '../components/LabelUnit'
import ColumnRadioButton from '../components/ColumnRadioButton'
import { PowerPurchaseBoard } from '../components/PowerPurchaseBoard'
import { useCallback, useEffect, useRef, useState } from 'react'
import ColumnRadioCostRatio from '../components/ColumnRadioCostRatio'
import TOUHeatMap from '../components/graph/TOUHeatMap'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
  Body,
  ContentWrapper,
  Header,
  BodyWrapper,
  PageWrapper,
  DetailColWrapper,
  DetailFrame,
  DetailWrapper,
  GraphBody,
  GraphFrame,
  GraphWrapper,
  LabelTable,
  Left,
  MonthChip,
  MonthChipText,
  MonthChipWrapper,
  OptionDetailBox,
  OptionDetailWrapper,
  OptionListWrapper,
  OptionTextBox,
  PvCustomerSummaryBox,
  PvCustomerSummaryItem,
  PvCustomerSummaryLabel,
  PvCustomerSummaryValue,
  Rectangle,
  Right,
  SeasonBox,
  SeasonFrame,
  SeasonTextBox,
  Table,
  TableCell,
  TableCell2,
  TableRow,
  TableWrapper,
  TargetRevenueCard,
  TargetRevenueDeltaPreview,
  TargetRevenueField,
  TargetRevenueFieldGrid,
  TargetRevenueFieldHelp,
  TargetRevenueFieldLabel,
  TargetRevenueInputBox,
  TargetRevenueInputRow,
  TargetRevenueResult,
  TargetRevenueResultLabel,
  TargetRevenueResultValue,
  TargetRevenueToggleButton,
  TargetSeasonCard,
  TargetSeasonGrid,
  TargetSeasonHeader,
  TargetSeasonMetric,
  TargetSeasonMetricLabel,
  TargetSeasonMetricValue,
  TargetRevenueSummary,
  TargetRevenueSummaryLabel,
  TargetRevenueSummaryValue,
  TargetRevenueUnit,
  Text,
  TextBox,
  TextBox2,
  TextWrapper,
} from './PurchasePlans.styled'
import {
  CollapsibleSectionTitle,
  SectionDivider,
  SectionFrame,
  SectionHeader,
  SectionMark,
  SectionTitle,
  SectionTitleChevron,
  SectionTitleToggleText,
} from '../style/SellPlans.styled'
import { seasonMapping } from '../util/algorithm/fakeRatesData'
import { calKepcoAverage } from '../util/algorithm/kepcoTOU'
import { calSmpTimeRate } from '../util/algorithm/smp'
import {
  CreateBuyTOUGQL,
  DeleteBuyTOUGQL,
  UpdateBuyTOUGQL,
  findAllBuyTOUGQL,
  findOneBuyTOUGQL,
  findPVDataGQL,
  getHolidaysGQL,
  findOneKepcoTOUGQL,
  getRECGQL,
} from '../gql/PurchasePlans.gql'
import { findAllSmpGQL } from './DataSMP'
import {
  calSeasonalSum,
  monthLoadTimeToSeasonLoadTime,
} from '../util/algorithm/monthToSeason'
import {
  IChargeUnit,
  ISeasonalNum,
  ITimeDataItem,
  ITouPeriods,
  ITouRate,
} from '../util/algorithm/algoInterface'
import {
  INIT_KEPCO_TOU_PERIODS,
  INIT_SINGLE_TOU_PERIODS,
  INIT_TOU_RATE,
} from '../util/contants'
import { calLoadTimeSum, divTimeData } from '../util/algorithm/classifyTimeData'
import { calMonthRepPattern } from '../util/algorithm/calRepPattern'
import {
  calFlatTouUnitCharge,
  calMinChargeUnit,
  calTouUnitCharge,
} from '../util/algorithm/makeTOU'
import { subtractPrevProfitToTradingCharge } from '../util/algorithm/calExCharge'
import { findAllUnitDefaultGQL } from './DataUnit'
import MonthLineGraph2 from '../components/graph/MonthLineGraph2'
import { findAllPVcusGQL } from '../gql/CustomerPV.gql'
import TableInput from '../components/TableInput'
import {
  calculateSeasonalPvRecRevenue,
  calculateSeasonalPvPreviousRevenue,
  getPvRevenueContractType,
  getRevenueContractLabel,
  isUnitPriceRevenueContract,
  normalizeRecOptForPvContract,
} from '../util/algorithm/pvRevenue'

export interface IPurchasePlansData {
  name: string //요금제명
  cust: any // PV 고객 아이디
  planType: string // 요금제 산정 방식, [tou, flat]
  pvOpt: string // PV 데이터 옵션, [realTime, aver, mid, mode]
  powerMarketTransactionUnit: number // 전력시장 수수료 단가
  touVer: any // 한전 TOU 요금제 버전
  recYear: any // 현물 REC 버전(연도)
  recOpt: string // REC 포함 여부, [energyOnly, energyAndRec]
  recRegion: string // REC 지역, [land, jeju]
  smpYear: any // SMP 버전(연도)
  holidayYear: any // 공휴일 버전(연도)
  smpOpt: string // 수익계산 SMP 옵션, [realTime, min, max, weightedAver]
  timeTableOpt: string // TOU 시간대 옵션, [kepco, smp, single]
  rateOpt: string // 가격 비율 옵션, [kepco, smp, single, userInput]
  flatAddEarnings: number // 단일단가 요금제 연간 조정 금액
  sprAddEarnings: number // 봄 조정 금액
  sumAddEarnings: number // 여름 조정 금액
  fallAddEarnings: number // 가을 조정 금액
  winAddEarnings: number // 겨울 조정 금액
  chargeUnitResult?: IChargeUnit // 요금제 결과 단가
  touPeriodResult?: ITouPeriods // 요금제 결과 시간대
  sprMinMidRate?: number // 봄 최저중간비율
  sprMinMaxRate?: number // 봄 최저최대비율
  sumMinMidRate?: number // 여름 최저중간비율
  sumMinMaxRate?: number // 여름 최저최대비율
  fallMinMidRate?: number // 가을 최저중간비율
  fallMinMaxRate?: number // 가을 최저최대비율
  winMinMidRate?: number // 겨율 최저중간비율
  winMinMaxRate?: number // 겨울 최저최대비율
  _id?: string
  index?: string
}

export const initializePurchasePlansData = (): IPurchasePlansData => {
  return {
    name: '',
    cust: '',
    planType: 'flat',
    pvOpt: 'realTime',
    powerMarketTransactionUnit: 0,
    touVer: '',
    recYear: '',
    recOpt: 'energyAndRec',
    recRegion: 'land',
    smpYear: '',
    holidayYear: '',
    smpOpt: 'realTime',
    timeTableOpt: 'flat',
    rateOpt: 'flat',
    flatAddEarnings: 0,
    sprAddEarnings: 0,
    sumAddEarnings: 0,
    fallAddEarnings: 0,
    winAddEarnings: 0,
    chargeUnitResult: {
      spr: {
        min: 0,
        mid: 0,
        max: 0,
      },
      sum: {
        min: 0,
        mid: 0,
        max: 0,
      },
      fall: {
        min: 0,
        mid: 0,
        max: 0,
      },
      win: {
        min: 0,
        mid: 0,
        max: 0,
      },
    },
    touPeriodResult: INIT_SINGLE_TOU_PERIODS,
    sprMinMidRate: 1,
    sprMinMaxRate: 1,
    sumMinMidRate: 1,
    sumMinMaxRate: 1,
    fallMinMidRate: 1,
    fallMinMaxRate: 1,
    winMinMidRate: 1,
    winMinMaxRate: 1,
  }
}

const roundUnit = (value: number, fractionDigits: number) => {
  const multiplier = 10 ** fractionDigits
  return Math.round(value * multiplier) / multiplier
}

const roundChargeUnitResult = (
  chargeUnit: IChargeUnit,
  fractionDigits: number
): IChargeUnit => ({
  spr: {
    min: roundUnit(chargeUnit.spr.min, fractionDigits),
    mid: roundUnit(chargeUnit.spr.mid, fractionDigits),
    max: roundUnit(chargeUnit.spr.max, fractionDigits),
  },
  sum: {
    min: roundUnit(chargeUnit.sum.min, fractionDigits),
    mid: roundUnit(chargeUnit.sum.mid, fractionDigits),
    max: roundUnit(chargeUnit.sum.max, fractionDigits),
  },
  fall: {
    min: roundUnit(chargeUnit.fall.min, fractionDigits),
    mid: roundUnit(chargeUnit.fall.mid, fractionDigits),
    max: roundUnit(chargeUnit.fall.max, fractionDigits),
  },
  win: {
    min: roundUnit(chargeUnit.win.min, fractionDigits),
    mid: roundUnit(chargeUnit.win.mid, fractionDigits),
    max: roundUnit(chargeUnit.win.max, fractionDigits),
  },
})

const sumSeasonalNum = (seasonalNum: ISeasonalNum) =>
  seasonalNum.spr + seasonalNum.sum + seasonalNum.fall + seasonalNum.win

const getPurchasePlanCalculationKey = (data: IPurchasePlansData) =>
  JSON.stringify({
    cust: data.cust,
    planType: data.planType,
    pvOpt: data.cust?.pvOpt || data.pvOpt || 'realTime',
    powerMarketTransactionUnit: data.powerMarketTransactionUnit,
    touVer: data.touVer,
    recYear: data.recYear,
    recOpt: data.recOpt,
    recRegion: data.recRegion,
    smpYear: data.smpYear,
    holidayYear: data.holidayYear,
    smpOpt: data.smpOpt,
    timeTableOpt: data.timeTableOpt,
    rateOpt: data.rateOpt,
    flatAddEarnings: data.flatAddEarnings,
    sprAddEarnings: data.sprAddEarnings,
    sumAddEarnings: data.sumAddEarnings,
    fallAddEarnings: data.fallAddEarnings,
    winAddEarnings: data.winAddEarnings,
    touPeriodResult: data.touPeriodResult,
    sprMinMidRate: data.sprMinMidRate,
    sprMinMaxRate: data.sprMinMaxRate,
    sumMinMidRate: data.sumMinMidRate,
    sumMinMaxRate: data.sumMinMaxRate,
    fallMinMidRate: data.fallMinMidRate,
    fallMinMaxRate: data.fallMinMaxRate,
    winMinMidRate: data.winMinMidRate,
    winMinMaxRate: data.winMinMaxRate,
  })

export const PurchasePlans = () => {
  const [purChasePlansData, setPurChasePlansData] =
    useState<IPurchasePlansData>(initializePurchasePlansData)
  const [selectedMonths, setSelectedMonths] = useState<number[]>([1])
  const [selectedPatternValue, setSelectedPatternValue] = useState('realTime')

  const { data: originPVCusList } = useQuery(findAllPVcusGQL)
  const [PVCusList, setPVCusList] = useState<any>()
  const effectivePvOpt =
    purChasePlansData.cust?.pvOpt || purChasePlansData.pvOpt || 'realTime'

  const { data: unitDefault, refetch: dataRefetch } = useQuery(
    findAllUnitDefaultGQL
  )
  const [resetState, setResetState] = useState(0)
  const [loading, setLoading] = useState(true)

  const [touPeriodsData, setTouPeriodsData] = useState<any>()

  const [findKepcoData] = useLazyQuery(findOneKepcoTOUGQL)
  // 한전요금제
  const [kepcoData, setKepcoData] = useState<any>()
  const [findPVData] = useLazyQuery(findPVDataGQL)

  //PV 데이터
  const [PVData, setPVData] = useState<any>()

  const [findHolidaysData] = useLazyQuery(getHolidaysGQL)

  //공휴일 데이터
  const [holidaysData, setHolidaysData] = useState<any>()

  const [findRECData] = useLazyQuery(getRECGQL)

  //REC 데이터
  const [recData, setRecData] = useState<any>()

  //smp 데이터
  const [realSmpData, setRealSmpData] = useState<any>()
  const [extraSmpData, setExtraSmpData] = useState<any>()

  const [isModify, setIsModify] = useState(false)
  const [isCheck, setIsCheck] = useState(false)

  const [findOnePurchasePlanData, { data: queryData, refetch }] = useLazyQuery(
    findOneBuyTOUGQL,
    {
      fetchPolicy: 'network-only',
    }
  )
  const [createBuyTOU] = useMutation(CreateBuyTOUGQL)
  const [updateBuyTOU] = useMutation(UpdateBuyTOUGQL)
  const [deleteBuyTOU] = useMutation(DeleteBuyTOUGQL)

  const [recOptRadio, setRecOptRadio] = useState('0')
  const [recRegionRadio, setRecRegionRadio] = useState('0')
  const [planTypeRadio, setPlanTypeRadio] = useState('0')
  const [timeTableOptRadio, setTimeTableOptRadio] = useState('0')
  const [rateOptRadio, setRateOptRadio] = useState('0')
  const [isTouRateSectionCollapsed, setIsTouRateSectionCollapsed] =
    useState(true)
  const [isTouSeasonDetailOpen, setIsTouSeasonDetailOpen] = useState(false)

  const { data: totalSmpData } = useQuery(findAllSmpGQL)

  // 옵션따라 고른 PV 데이터
  const [usingPvData, setUsingPvData] = useState<ITimeDataItem[]>([])
  // 옵션따라 고른 PV 데이터 그래프용
  const [pvGraph, setPvGraph] = useState<ITimeDataItem[]>([])
  //수익계산에 사용할 smp 데이터
  const [useForCostSmpData, setUseForCostSmpData] = useState<ITimeDataItem[]>(
    []
  )
  //계절별 전력거래 합
  const [seasonalPVSum, setSeasonalPVSum] = useState<ISeasonalNum>({
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  })
  const [touType, setTouType] = useState<ITouPeriods | undefined>(undefined)

  //smp로 계산한 비율
  const [smpRate, setSmpRate] = useState<ITouRate>(INIT_TOU_RATE)
  //smp로 계산한 시간대
  const [smpTimePeriod, setSmpTimePeriod] = useState()
  // 한전 요금제로 계산한 비율
  const [kepcoTouRate, setKepcoTouRate] = useState<ITouRate>(INIT_TOU_RATE)
  // 기존수익
  const [prevProfit, setPrevProfit] = useState<ISeasonalNum>({
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  })
  const [seasonalRecAveragePrice, setSeasonalRecAveragePrice] =
    useState<ISeasonalNum>({
      spr: 0,
      sum: 0,
      fall: 0,
      win: 0,
    })
  // 기존수익+추가수익
  const [prevPlusAddProfit, setPrevPlusAddProfit] = useState<ISeasonalNum>({
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  })
  // 요금제 비율(purchasePlans에 따로 저장해버려서 다시 저장..)
  const [touRate, setTouRate] = useState<ITouRate>(INIT_TOU_RATE)
  //최종수익
  const [recExpectedProfit, setRecExpectedProfit] = useState<ISeasonalNum>({
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  })

  const [listRefetch, setListRefetch] = useState<() => void>(() => () => {})
  const loadedChargeUnitResult = useRef<IChargeUnit | undefined>(undefined)
  const loadedCalculationKey = useRef<string | undefined>(undefined)

  const handleRefetch = useCallback(() => {
    if (listRefetch) {
      listRefetch()
    }
  }, [listRefetch])

  const handleFindOne = (id: string) => {
    findOnePurchasePlanData({ variables: { val: id } })
  }

  const selectedRevenueContractType = getPvRevenueContractType(
    purChasePlansData.cust
  )
  const selectedPvCustomer =
    purChasePlansData.cust && typeof purChasePlansData.cust === 'object'
      ? purChasePlansData.cust
      : undefined
  const selectedPvContractLabel = getRevenueContractLabel(
    selectedRevenueContractType
  )
  const selectedPvSummaryContractLabel =
    selectedRevenueContractType === 'kepcoPpa'
      ? '한전 PPA'
      : selectedPvContractLabel
  const selectedPvContractPrice = Number(selectedPvCustomer?.contractPrice ?? 0)
  const selectedPvContractPriceText =
    isUnitPriceRevenueContract(selectedRevenueContractType) &&
    selectedPvContractPrice > 0
      ? ` ${selectedPvContractPrice.toLocaleString()}원/kWh`
      : ''
  const formatSelectedPvNumber = (value: unknown, unit = '') => {
    const numberValue = Number(value)
    if (!Number.isFinite(numberValue)) return '-'

    return `${numberValue.toLocaleString()}${unit}`
  }
  const formatDefaultValue = (value: unknown, unit = '') => {
    if (value === null || value === undefined || value === '') return '-'

    return `${value}${unit}`
  }
  const getPurchaseUnitDefaultValues = useCallback((unitDefaultData: any) => {
    const defaultData = unitDefaultData?.findAllUnitDefault?.[0]
    if (!defaultData) return {}

    return {
      powerMarketTransactionUnit: defaultData.powerMarketTransactionUnit ?? 0,
      touVer: defaultData.touVer ?? '',
      recYear: defaultData.recYear ?? '',
      smpYear: defaultData.smpYear ?? '',
      holidayYear: defaultData.holidayYear ?? '',
    }
  }, [])

  useEffect(() => {
    if (queryData && queryData.findOneBuyTOU) {
      const {
        __typename,
        planType,
        flatAddEarnings,
        recOpt,
        recRegion,
        ...rest
      } = queryData.findOneBuyTOU
      const normalizedPlanType =
        planType ||
        (rest.timeTableOpt === 'flat' || rest.rateOpt === 'flat'
          ? 'flat'
          : 'tou')
      const normalizedFlatAddEarnings =
        flatAddEarnings ??
        (normalizedPlanType === 'flat'
          ? (rest.sprAddEarnings || 0) +
            (rest.sumAddEarnings || 0) +
            (rest.fallAddEarnings || 0) +
            (rest.winAddEarnings || 0)
          : 0)

      const loadedPurchasePlan = {
        ...initializePurchasePlansData(),
        ...rest,
        planType: normalizedPlanType,
        flatAddEarnings: normalizedFlatAddEarnings,
        recOpt: recOpt || 'energyAndRec',
        recRegion: recRegion || 'land',
        ...getPurchaseUnitDefaultValues(unitDefault),
      }

      loadedChargeUnitResult.current = loadedPurchasePlan.chargeUnitResult
      loadedCalculationKey.current =
        getPurchasePlanCalculationKey(loadedPurchasePlan)
      setPurChasePlansData(loadedPurchasePlan)
    }
  }, [queryData, unitDefault, getPurchaseUnitDefaultValues])

  useEffect(() => {
    const revenueContractType = getPvRevenueContractType(purChasePlansData.cust)
    const nextSmpOpt = 'realTime'
    const nextRecOpt = normalizeRecOptForPvContract(
      revenueContractType,
      purChasePlansData.recOpt
    )

    if (
      nextSmpOpt !== purChasePlansData.smpOpt ||
      nextRecOpt !== purChasePlansData.recOpt
    ) {
      setPurChasePlansData((prev) => ({
        ...prev,
        smpOpt: nextSmpOpt,
        recOpt: nextRecOpt,
      }))
    }
  }, [
    purChasePlansData.cust,
    purChasePlansData.smpOpt,
    purChasePlansData.recOpt,
  ])

  useEffect(() => {
    const fetchData = async () => {
      await dataRefetch()
      setLoading(false)
    }

    fetchData()
  }, [dataRefetch])

  useEffect(() => {
    if (unitDefault && unitDefault.findAllUnitDefault) {
      setPurChasePlansData((prev) => ({
        ...prev,
        ...getPurchaseUnitDefaultValues(unitDefault),
      }))
    }
    setSelectedMonths([1])
  }, [
    loading,
    unitDefault,
    setPurChasePlansData,
    resetState,
    getPurchaseUnitDefaultValues,
  ])

  useEffect(() => {
    if (unitDefault && unitDefault.findAllUnitDefault) {
      setPurChasePlansData((prev) => ({
        ...prev,
        ...getPurchaseUnitDefaultValues(unitDefault),
      }))
    }
  }, [unitDefault, setPurChasePlansData, getPurchaseUnitDefaultValues])

  // [수정] SMP 연도 변경 시 데이터 세팅 (타입 변환 추가)
  useEffect(() => {
    if (
      purChasePlansData.smpYear &&
      purChasePlansData.smpYear !== '' &&
      totalSmpData &&
      totalSmpData.findAllSmp
    ) {
      // String()으로 변환하여 비교 (DB에선 숫자, 리스트엔 문자로 있을 수 있음)
      const selectedYearData = totalSmpData.findAllSmp.find(
        (entry: any) => String(entry.year) === String(purChasePlansData.smpYear)
      )

      if (selectedYearData) {
        const realSmpData = selectedYearData.data.map(
          ({ date, value }: { date: string; value: number }) => ({
            date,
            value,
          })
        )
        const realSmpDataWithYear = {
          year: selectedYearData.year,
          data: realSmpData,
        }
        const extraSmpData = selectedYearData.data.map(
          ({
            date,
            minSmp,
            maxSmp,
            weightedAver,
          }: {
            date: string
            minSmp: number
            maxSmp: number
            weightedAver: number
          }) => ({ date, minSmp, maxSmp, weightedAver })
        )
        const extraSmpDataWithYear = {
          year: selectedYearData.year,
          data: extraSmpData,
        }
        setRealSmpData(realSmpDataWithYear)
        setExtraSmpData(extraSmpDataWithYear)
      }
    }
  }, [purChasePlansData.smpYear, totalSmpData])

  useEffect(() => {
    if (purChasePlansData.planType === 'flat') {
      setTouType(INIT_SINGLE_TOU_PERIODS)
    } else if (purChasePlansData.timeTableOpt === 'kepco') {
      if (touPeriodsData !== undefined) {
        setTouType(touPeriodsData)
      }
    } else if (purChasePlansData.timeTableOpt === 'smp') {
      setTouType(smpTimePeriod)
    } else {
      setTouType(INIT_SINGLE_TOU_PERIODS)
    }
  }, [
    purChasePlansData.planType,
    purChasePlansData.timeTableOpt,
    touPeriodsData,
    purChasePlansData.holidayYear,
    purChasePlansData.smpYear,
    timeTableOptRadio,
    smpTimePeriod,
  ])

  useEffect(() => {
    handleResetAll()
  }, [])

  useEffect(() => {
    setIsModify(!!purChasePlansData._id)
  }, [purChasePlansData._id])

  useEffect(() => {
    if (originPVCusList) setPVCusList(Object.values(originPVCusList)[0])
  }, [originPVCusList])

  useEffect(() => {
    switch (purChasePlansData.recOpt) {
      case 'energyAndRec':
        setRecOptRadio('0')
        break
      case 'energyOnly':
      default:
        setRecOptRadio('1')
        break
    }
  }, [purChasePlansData.recOpt])

  useEffect(() => {
    switch (purChasePlansData.recRegion) {
      case 'jeju':
        setRecRegionRadio('1')
        break
      case 'land':
      default:
        setRecRegionRadio('0')
        break
    }
  }, [purChasePlansData.recRegion])

  useEffect(() => {
    switch (purChasePlansData.timeTableOpt) {
      case 'flat':
        setTimeTableOptRadio('0')
        break
      case 'smp':
        setTimeTableOptRadio('1')
        break
      case 'single':
        setTimeTableOptRadio('2')
        break
      case 'kepco':
      default:
        setTimeTableOptRadio('0')
        break
    }
  }, [purChasePlansData.timeTableOpt])

  useEffect(() => {
    switch (purChasePlansData.planType) {
      case 'flat':
        setPlanTypeRadio('0')
        break
      case 'tou':
      default:
        setPlanTypeRadio('1')
        break
    }
  }, [purChasePlansData.planType])

  useEffect(() => {
    switch (purChasePlansData.rateOpt) {
      case 'flat':
        setRateOptRadio('0')
        break
      case 'smp':
        setRateOptRadio('1')
        if (smpRate) {
          setPurChasePlansData((prevData) => ({
            ...prevData,
            sprMinMidRate: smpRate.spr.minMid,
            sumMinMidRate: smpRate.sum.minMid,
            fallMinMidRate: smpRate.fall.minMid,
            winMinMidRate: smpRate.win.minMid,
            sprMinMaxRate: smpRate.spr.minMax,
            sumMinMaxRate: smpRate.sum.minMax,
            fallMinMaxRate: smpRate.fall.minMax,
            winMinMaxRate: smpRate.win.minMax,
          }))
        }

        break
      case 'single':
        setRateOptRadio('2')
        setPurChasePlansData((prevData) => ({
          ...prevData,
          sprMinMidRate: 1,
          sumMinMidRate: 1,
          fallMinMidRate: 1,
          winMinMidRate: 1,
          sprMinMaxRate: 1,
          sumMinMaxRate: 1,
          fallMinMaxRate: 1,
          winMinMaxRate: 1,
        }))

        break
      case 'userInput':
        setRateOptRadio('3')
        break
      case 'kepco':
      default:
        setRateOptRadio('0')
        if (kepcoData) {
          setKepcoTouRate(calKepcoAverage(kepcoData))
          setPurChasePlansData((prevData) => ({
            ...prevData,
            sprMinMidRate: kepcoTouRate.spr.minMid,
            sumMinMidRate: kepcoTouRate.sum.minMid,
            fallMinMidRate: kepcoTouRate.fall.minMid,
            winMinMidRate: kepcoTouRate.win.minMid,
            sprMinMaxRate: kepcoTouRate.spr.minMax,
            sumMinMaxRate: kepcoTouRate.sum.minMax,
            fallMinMaxRate: kepcoTouRate.fall.minMax,
            winMinMaxRate: kepcoTouRate.win.minMax,
          }))
        }
        break
    }
  }, [purChasePlansData.rateOpt, kepcoData, smpRate])

  useEffect(() => {
    if (!purChasePlansData.cust?._id) return

    findPVData({
      variables: {
        val: purChasePlansData?.cust?._id,
      },
    }).then((result: any) => {
      const data = result.data ? Object.values(result.data)[0] : undefined

      if (result.data) setPVData(data)
    })
  }, [effectivePvOpt, findPVData, purChasePlansData.cust])
  // 계절별 전력거래 합 구하기
  useEffect(() => {
    if (usingPvData && PVData) {
      setSeasonalPVSum(calSeasonalSum(usingPvData, seasonMapping))
    }
  }, [usingPvData])

  // smp, 공휴일 고르면 smp 비율, smp 시간대 계산
  useEffect(() => {
    if (realSmpData && seasonMapping && holidaysData) {
      const smpValue = calSmpTimeRate(
        realSmpData.data,
        seasonMapping,
        holidaysData
      )
      setSmpRate(smpValue.smpRate)
      setSmpTimePeriod(smpValue.smpTimePeriod)
    }
  }, [realSmpData, seasonMapping, holidaysData])

  //기존 수익 계산
  useEffect(() => {
    if (usingPvData.length === 0) return

    const revenue = calculateSeasonalPvPreviousRevenue({
      customer: purChasePlansData.cust,
      usingPvData,
      seasonalPVSum,
      smpData: useForCostSmpData,
      seasonMapping,
      recYear: purChasePlansData.recYear,
      seasonalRecAveragePrice,
    })

    if (revenue) setPrevProfit(revenue)
  }, [
    useForCostSmpData,
    usingPvData,
    purChasePlansData.cust,
    purChasePlansData.recYear,
    seasonalRecAveragePrice,
    seasonalPVSum,
  ])

  // 기존 기준 금액 + 조정 금액 계산
  useEffect(() => {
    const seasonalRecRevenue = calculateSeasonalPvRecRevenue({
      customer: purChasePlansData.cust,
      seasonalPVSum,
      recYear: purChasePlansData.recYear,
      seasonalRecAveragePrice,
    })
    const ratePlanBaseProfit =
      purChasePlansData.recOpt === 'energyAndRec'
        ? prevProfit
        : subtractPrevProfitToTradingCharge(prevProfit, seasonalRecRevenue)
    const tmp = {
      spr: 0,
      sum: 0,
      fall: 0,
      win: 0,
    }
    if (purChasePlansData.planType === 'flat') {
      const flatShare = purChasePlansData.flatAddEarnings / 4
      tmp.spr = ratePlanBaseProfit.spr + flatShare
      tmp.sum = ratePlanBaseProfit.sum + flatShare
      tmp.fall = ratePlanBaseProfit.fall + flatShare
      tmp.win = ratePlanBaseProfit.win + flatShare
    } else {
      tmp.spr = ratePlanBaseProfit.spr + purChasePlansData.sprAddEarnings
      tmp.sum = ratePlanBaseProfit.sum + purChasePlansData.sumAddEarnings
      tmp.fall = ratePlanBaseProfit.fall + purChasePlansData.fallAddEarnings
      tmp.win = ratePlanBaseProfit.win + purChasePlansData.winAddEarnings
    }

    setPrevPlusAddProfit(tmp)
  }, [
    purChasePlansData.planType,
    purChasePlansData.cust,
    purChasePlansData.recOpt,
    purChasePlansData.recYear,
    purChasePlansData.flatAddEarnings,
    prevProfit,
    seasonalPVSum,
    seasonalRecAveragePrice,
    purChasePlansData.sprAddEarnings,
    purChasePlansData.sumAddEarnings,
    purChasePlansData.fallAddEarnings,
    purChasePlansData.winAddEarnings,
  ])

  //touRate 세팅
  useEffect(() => {
    const hasAllTouRates = [
      purChasePlansData.sprMinMidRate,
      purChasePlansData.sprMinMaxRate,
      purChasePlansData.sumMinMidRate,
      purChasePlansData.sumMinMaxRate,
      purChasePlansData.fallMinMidRate,
      purChasePlansData.fallMinMaxRate,
      purChasePlansData.winMinMidRate,
      purChasePlansData.winMinMaxRate,
    ].every((rate) => typeof rate === 'number' && !Number.isNaN(rate))

    if (hasAllTouRates) {
      const {
        sprMinMidRate,
        sprMinMaxRate,
        sumMinMidRate,
        sumMinMaxRate,
        fallMinMidRate,
        fallMinMaxRate,
        winMinMidRate,
        winMinMaxRate,
      } = purChasePlansData as Required<
        Pick<
          IPurchasePlansData,
          | 'sprMinMidRate'
          | 'sprMinMaxRate'
          | 'sumMinMidRate'
          | 'sumMinMaxRate'
          | 'fallMinMidRate'
          | 'fallMinMaxRate'
          | 'winMinMidRate'
          | 'winMinMaxRate'
        >
      >

      const touRate: ITouRate = {
        spr: {
          minMid: sprMinMidRate,
          minMax: sprMinMaxRate,
        },
        sum: {
          minMid: sumMinMidRate,
          minMax: sumMinMaxRate,
        },
        fall: {
          minMid: fallMinMidRate,
          minMax: fallMinMaxRate,
        },
        win: {
          minMid: winMinMidRate,
          minMax: winMinMaxRate,
        },
      }

      setTouRate(touRate)
    }
  }, [
    purChasePlansData.sprMinMidRate,
    purChasePlansData.sprMinMaxRate,
    purChasePlansData.sumMinMidRate,
    purChasePlansData.sumMinMaxRate,
    purChasePlansData.fallMinMidRate,
    purChasePlansData.fallMinMaxRate,
    purChasePlansData.winMinMidRate,
    purChasePlansData.winMinMaxRate,
  ])

  //요금제 산출 및 계절별 요금 계산
  useEffect(() => {
    if (
      usingPvData.length !== 0 &&
      seasonMapping &&
      holidaysData &&
      purChasePlansData.touPeriodResult &&
      touRate
    ) {
      const monthlyTimePeriodGen = calLoadTimeSum(
        usingPvData,
        seasonMapping,
        holidaysData,
        purChasePlansData.touPeriodResult
      ).loadTimeSumResult

      // 월별 발전량> 계절별 발전량
      const seasonalTimePeriodGen = monthLoadTimeToSeasonLoadTime(
        monthlyTimePeriodGen,
        seasonMapping
      )

      const calculatedTouUnit =
        purChasePlansData.planType === 'flat'
          ? calFlatTouUnitCharge(seasonalTimePeriodGen, prevPlusAddProfit)
          : calTouUnitCharge(
              touRate,
              calMinChargeUnit(
                touRate,
                seasonalTimePeriodGen,
                prevPlusAddProfit
              )
            )
      const touUnit = roundChargeUnitResult(
        calculatedTouUnit,
        purChasePlansData.planType === 'flat' ? 0 : 1
      )
      const shouldKeepLoadedChargeUnit =
        loadedChargeUnitResult.current &&
        loadedCalculationKey.current ===
          getPurchasePlanCalculationKey(purChasePlansData)
      const chargeUnitResult = shouldKeepLoadedChargeUnit
        ? loadedChargeUnitResult.current ?? touUnit
        : touUnit
      setPurChasePlansData((prevData) => ({
        ...prevData,
        chargeUnitResult,
      }))

      const seasonalRecRevenue = calculateSeasonalPvRecRevenue({
        customer: purChasePlansData.cust,
        seasonalPVSum,
        recYear: purChasePlansData.recYear,
        seasonalRecAveragePrice,
      })
      setRecExpectedProfit(
        purChasePlansData.recOpt === 'energyAndRec'
          ? {
              spr: 0,
              sum: 0,
              fall: 0,
              win: 0,
            }
          : seasonalRecRevenue
      )
    }
  }, [
    usingPvData,
    holidaysData,
    purChasePlansData.cust,
    purChasePlansData.planType,
    purChasePlansData.recOpt,
    purChasePlansData.recYear,
    purChasePlansData.touPeriodResult,
    prevPlusAddProfit,
    seasonalPVSum,
    seasonalRecAveragePrice,
    touRate,
  ])

  useEffect(() => {
    if (purChasePlansData.holidayYear && purChasePlansData.holidayYear !== '') {
      findHolidaysData({
        variables: {
          val: String(purChasePlansData.holidayYear),
        },
      }).then((result: any) => {
        //@ts-ignore
        if (result.data) setHolidaysData(Object.values(result.data)[0].data)
      })
    }
  }, [purChasePlansData.holidayYear, purChasePlansData._id])

  useEffect(() => {
    if (purChasePlansData.touVer && purChasePlansData.touVer !== '') {
      findKepcoData({
        variables: {
          val: purChasePlansData?.touVer,
        },
      }).then((result: any) => {
        //@ts-ignore
        if (result.data) {
          const kepcoTOU = Object.values(result.data)[0] as any
          setKepcoData(kepcoTOU.data)
          setTouPeriodsData(
            kepcoTOU.kepcoTOUPeriod?.touPeriods || INIT_KEPCO_TOU_PERIODS
          )
        }
      })
    }
  }, [purChasePlansData.touVer])

  useEffect(() => {
    if (purChasePlansData.recYear && purChasePlansData.recYear !== '') {
      findRECData({
        variables: {
          val: String(purChasePlansData.recYear),
        },
      }).then((result: any) => {
        //@ts-ignore
        if (result.data) setRecData(Object.values(result.data)[0][0].data)
      })
    }
  }, [purChasePlansData.recYear, purChasePlansData._id])

  useEffect(() => {
    if (
      purChasePlansData.recYear &&
      purChasePlansData.recYear !== '' &&
      Array.isArray(recData) &&
      recData.length > 0
    ) {
      const priceKey =
        purChasePlansData.recRegion === 'jeju' ? 'jejuAvgPrc' : 'landAvgPrc'
      const seasonalPriceSum: ISeasonalNum = {
        spr: 0,
        sum: 0,
        fall: 0,
        win: 0,
      }
      const seasonalPriceCount: ISeasonalNum = {
        spr: 0,
        sum: 0,
        fall: 0,
        win: 0,
      }

      recData.forEach((item: any) => {
        const month = new Date(item.date).getMonth() + 1
        const season = seasonMapping[month] as keyof ISeasonalNum
        const price = Number(item[priceKey])

        if (season && !Number.isNaN(price)) {
          seasonalPriceSum[season] += price
          seasonalPriceCount[season] += 1
        }
      })

      setSeasonalRecAveragePrice({
        spr:
          seasonalPriceCount.spr === 0
            ? 0
            : Math.round(seasonalPriceSum.spr / seasonalPriceCount.spr),
        sum:
          seasonalPriceCount.sum === 0
            ? 0
            : Math.round(seasonalPriceSum.sum / seasonalPriceCount.sum),
        fall:
          seasonalPriceCount.fall === 0
            ? 0
            : Math.round(seasonalPriceSum.fall / seasonalPriceCount.fall),
        win:
          seasonalPriceCount.win === 0
            ? 0
            : Math.round(seasonalPriceSum.win / seasonalPriceCount.win),
      })
    } else {
      setSeasonalRecAveragePrice({
        spr: 0,
        sum: 0,
        fall: 0,
        win: 0,
      })
    }
  }, [purChasePlansData.recYear, purChasePlansData.recRegion, recData])

  //시간대 설정
  useEffect(() => {
    if (purChasePlansData.planType === 'flat') {
      setPurChasePlansData((prevData) => ({
        ...prevData,
        touPeriodResult: INIT_SINGLE_TOU_PERIODS,
      }))
      return
    }

    switch (purChasePlansData.timeTableOpt) {
      case 'flat':
        setPurChasePlansData((prevData) => ({
          ...prevData,
          touPeriodResult: INIT_SINGLE_TOU_PERIODS,
        }))
        break
      case 'smp':
        setPurChasePlansData((prevData) => ({
          ...prevData,
          touPeriodResult: smpTimePeriod,
        }))
        break
      case 'single':
        setPurChasePlansData((prevData) => ({
          ...prevData,
          touPeriodResult: INIT_SINGLE_TOU_PERIODS,
        }))
        break
      case 'kepco':
      default:
        setPurChasePlansData((prevData) => ({
          ...prevData,
          touPeriodResult: touPeriodsData || INIT_KEPCO_TOU_PERIODS,
        }))
        break
    }
  }, [
    purChasePlansData.planType,
    purChasePlansData.smpYear,
    purChasePlansData.timeTableOpt,
    smpTimePeriod,
    touPeriodsData,
  ])

  const toggleMonth = (index: number) => {
    setSelectedMonths([index])
  }

  const handleDataChange = (key: string, newValue: number | string) => {
    if (typeof newValue === 'number') {
      if (isNaN(newValue)) {
        setPurChasePlansData((prev) => {
          const updatedDataUnit = { ...prev, [key]: 0 }
          return updatedDataUnit
        })
      } else {
        setPurChasePlansData((prev) => {
          const updatedDataUnit = { ...prev, [key]: newValue }
          return updatedDataUnit
        })
      }
    } else {
      setPurChasePlansData((prev) => {
        const updatedDataUnit = { ...prev, [key]: newValue }
        return updatedDataUnit
      })
    }
  }

  const handleSeasonCostInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
    newValue: number | string
  ) => {
    const { value } = e.target
    let inputValue = value

    if (inputValue === '') {
      inputValue = '0'
    }

    inputValue = inputValue.replace(/[^\d.-]/g, '')
    inputValue =
      (inputValue.startsWith('-') ? '-' : '') + inputValue.replace(/-/g, '')

    if (inputValue === '-') {
      e.target.value = inputValue
      return
    }

    const regex = /^-?\d+(\.\d*)?$/
    if (!regex.test(inputValue)) {
      inputValue = ''
    }

    newValue = parseFloat(inputValue)

    if (isNaN(newValue)) {
      newValue = 0
    }

    e.target.value = inputValue

    setPurChasePlansData((prev) => {
      const updateData = { ...prev, [key]: newValue }
      return updateData
    })
  }

  const handleFlatTargetUnitInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target
    const parsedValue = parseFloat(value)
    const targetUnit = Number.isNaN(parsedValue) ? 0 : Math.round(parsedValue)
    const totalLoad = sumSeasonalNum(seasonalPVSum)
    const seasonalRecRevenue = calculateSeasonalPvRecRevenue({
      customer: purChasePlansData.cust,
      seasonalPVSum,
      recYear: purChasePlansData.recYear,
      seasonalRecAveragePrice,
    })
    const ratePlanBaseProfit =
      purChasePlansData.recOpt === 'energyAndRec'
        ? prevProfit
        : subtractPrevProfitToTradingCharge(prevProfit, seasonalRecRevenue)
    const flatAddEarnings =
      totalLoad === 0
        ? 0
        : Math.round(
            targetUnit * totalLoad - sumSeasonalNum(ratePlanBaseProfit)
          )

    setPurChasePlansData((prev) => ({
      ...prev,
      flatAddEarnings,
    }))
  }

  const handleTouAnnualEarningsInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target
    let inputValue = value

    if (inputValue === '') {
      inputValue = '0'
    }

    inputValue = inputValue.replace(/[^\d.-]/g, '')
    inputValue =
      (inputValue.startsWith('-') ? '-' : '') + inputValue.replace(/-/g, '')

    if (inputValue === '-') {
      e.target.value = inputValue
      return
    }

    const regex = /^-?\d+(\.\d*)?$/
    if (!regex.test(inputValue)) {
      inputValue = ''
    }

    const annualAddEarnings = parseFloat(inputValue)
    const normalizedAnnualAddEarnings = Number.isNaN(annualAddEarnings)
      ? 0
      : annualAddEarnings
    const baseSeasonAddEarnings =
      normalizedAnnualAddEarnings < 0
        ? Math.ceil(normalizedAnnualAddEarnings / 4)
        : Math.floor(normalizedAnnualAddEarnings / 4)
    const winterAddEarnings =
      normalizedAnnualAddEarnings - baseSeasonAddEarnings * 3

    e.target.value = inputValue

    setPurChasePlansData((prev) => ({
      ...prev,
      sprAddEarnings: baseSeasonAddEarnings,
      sumAddEarnings: baseSeasonAddEarnings,
      fallAddEarnings: baseSeasonAddEarnings,
      winAddEarnings: winterAddEarnings,
    }))
  }

  const total =
    purChasePlansData.planType === 'flat'
      ? purChasePlansData.flatAddEarnings
      : purChasePlansData.sprAddEarnings +
        purChasePlansData.sumAddEarnings +
        purChasePlansData.fallAddEarnings +
        purChasePlansData.winAddEarnings
  const flatPrevProfit = sumSeasonalNum(prevProfit)
  const flatTargetProfit = flatPrevProfit + purChasePlansData.flatAddEarnings
  const seasonalRecRevenueForTarget = calculateSeasonalPvRecRevenue({
    customer: purChasePlansData.cust,
    seasonalPVSum,
    recYear: purChasePlansData.recYear,
    seasonalRecAveragePrice,
  })
  const flatRatePlanBaseProfit =
    purChasePlansData.recOpt === 'energyAndRec'
      ? prevProfit
      : subtractPrevProfitToTradingCharge(
          prevProfit,
          seasonalRecRevenueForTarget
        )
  const flatTargetUnit =
    sumSeasonalNum(seasonalPVSum) === 0
      ? 0
      : Math.round(
          (sumSeasonalNum(flatRatePlanBaseProfit) +
            purChasePlansData.flatAddEarnings) /
            sumSeasonalNum(seasonalPVSum)
        )
  const flatEarningsTone =
    purChasePlansData.flatAddEarnings > 0
      ? 'positive'
      : purChasePlansData.flatAddEarnings < 0
        ? 'negative'
        : 'neutral'
  const touSeasonTargets = [
    {
      label: '봄',
      prevProfit: prevProfit.spr,
      addEarningsKey: 'sprAddEarnings',
      addEarnings: purChasePlansData.sprAddEarnings,
    },
    {
      label: '여름',
      prevProfit: prevProfit.sum,
      addEarningsKey: 'sumAddEarnings',
      addEarnings: purChasePlansData.sumAddEarnings,
    },
    {
      label: '가을',
      prevProfit: prevProfit.fall,
      addEarningsKey: 'fallAddEarnings',
      addEarnings: purChasePlansData.fallAddEarnings,
    },
    {
      label: '겨울',
      prevProfit: prevProfit.win,
      addEarningsKey: 'winAddEarnings',
      addEarnings: purChasePlansData.winAddEarnings,
    },
  ]
  const touTargetProfit = flatPrevProfit + total
  const touEarningsTone =
    total > 0 ? 'positive' : total < 0 ? 'negative' : 'neutral'

  // 차트관련
  useEffect(() => {
    setSelectedPatternValue(effectivePvOpt)
  }, [effectivePvOpt])

  // PV 선택
  useEffect(() => {
    if (PVData) {
      // PVData 직접 수정이 안되어서 복사
      // let tmpData = JSON.parse(JSON.stringify(PVData))
      const tmpData = divTimeData(PVData, 1000)

      switch (effectivePvOpt) {
        case 'aver':
          const monthRepPatternAver = calMonthRepPattern(tmpData, 'aver')
          setUsingPvData(monthRepPatternAver.repData)
          setPvGraph(monthRepPatternAver.graphData)

          break
        case 'mid':
          const monthRepPatternMid = calMonthRepPattern(tmpData, 'mid')
          setUsingPvData(monthRepPatternMid.repData)
          setPvGraph(monthRepPatternMid.graphData)

          break
        case 'mode':
          const monthRepPatternMode = calMonthRepPattern(tmpData, 'mode')
          setUsingPvData(monthRepPatternMode.repData)
          setPvGraph(monthRepPatternMode.graphData)

          break

        case 'realTime':
        default:
          setUsingPvData(tmpData)
          setPvGraph(tmpData)

          break
      }
    }
  }, [effectivePvOpt, PVData, selectedMonths])

  //smp
  // [추가] 원본 데이터(realSmpData)나 옵션(smpOpt)이 바뀌면 계산용 데이터(useForCostSmpData) 자동 갱신
  useEffect(() => {
    // 데이터가 아직 준비 안 됐으면 패스
    if (!realSmpData && !extraSmpData) return

    if (realSmpData) {
      setUseForCostSmpData(realSmpData.data)
      return
    }

    setUseForCostSmpData([])
  }, [
    purChasePlansData.smpOpt,
    purChasePlansData.cust,
    realSmpData,
    extraSmpData,
  ])

  const handleRecOptRadiobutton = (value: string) => {
    const revenueContractType = getPvRevenueContractType(purChasePlansData.cust)
    const recOpt = normalizeRecOptForPvContract(
      revenueContractType,
      value === '0' ? 'energyAndRec' : 'energyOnly'
    )

    switch (value) {
      case '0':
        setPurChasePlansData((prev) => ({
          ...prev,
          recOpt,
        }))
        break
      case '1':
      default:
        setPurChasePlansData((prev) => ({
          ...prev,
          recOpt,
        }))
        break
    }
  }

  const handleRecRegionRadiobutton = (value: string) => {
    switch (value) {
      case '1':
        setPurChasePlansData((prev) => ({
          ...prev,
          recRegion: 'jeju',
        }))
        break
      case '0':
      default:
        setPurChasePlansData((prev) => ({
          ...prev,
          recRegion: 'land',
        }))
        break
    }
  }

  const handlePlanTypeOpt = (value: string) => {
    switch (value) {
      case '0':
        setPurChasePlansData((prev) => ({
          ...prev,
          planType: 'flat',
          timeTableOpt: 'flat',
          rateOpt: 'flat',
          flatAddEarnings:
            prev.sprAddEarnings +
            prev.sumAddEarnings +
            prev.fallAddEarnings +
            prev.winAddEarnings,
          sprMinMidRate: 1,
          sumMinMidRate: 1,
          fallMinMidRate: 1,
          winMinMidRate: 1,
          sprMinMaxRate: 1,
          sumMinMaxRate: 1,
          fallMinMaxRate: 1,
          winMinMaxRate: 1,
        }))
        break
      case '1':
      default:
        setPurChasePlansData((prev) => ({
          ...prev,
          planType: 'tou',
          timeTableOpt:
            prev.timeTableOpt === 'flat' ? 'kepco' : prev.timeTableOpt,
          rateOpt: prev.rateOpt === 'flat' ? 'kepco' : prev.rateOpt,
        }))
        break
    }
  }

  const handleTimeTableOpt = (value: string) => {
    switch (value) {
      case '1':
        setPurChasePlansData((prev) => {
          return { ...prev, planType: 'tou', timeTableOpt: 'smp' }
        })
        break

      case '2':
        setPurChasePlansData((prev) => ({
          ...prev,
          planType: 'tou',
          timeTableOpt: 'single',
          rateOpt: 'single',
        }))
        break

      case '0':
      default:
        setPurChasePlansData((prev) => {
          return { ...prev, planType: 'tou', timeTableOpt: 'kepco' }
        })
        break
    }
  }

  const handleRateOpt = (value: string) => {
    switch (value) {
      case '1':
        setPurChasePlansData((prev) => {
          return { ...prev, planType: 'tou', rateOpt: 'smp' }
        })
        break
      case '2':
        setPurChasePlansData((prev) => ({
          ...prev,
          planType: 'tou',
          rateOpt: 'single',
          timeTableOpt: 'single',
        }))
        break
      case '3':
        setPurChasePlansData((prev) => {
          return { ...prev, planType: 'tou', rateOpt: 'userInput' }
        })
        break
      case '0':
      default:
        setPurChasePlansData((prev) => {
          return { ...prev, planType: 'tou', rateOpt: 'kepco' }
        })
        break
    }
  }

  const onSave = async () => {
    try {
      const { cust, recYear, smpYear, holidayYear, ...less } = purChasePlansData
      delete (less as any).pvOpt

      const custId = cust._id
      const recYearNum = Number(recYear)
      const smpYearNum = Number(smpYear)
      const holidayYearNum = Number(holidayYear)
      const revenueContractType = getPvRevenueContractType(cust)
      const recOpt = normalizeRecOptForPvContract(
        revenueContractType,
        purChasePlansData.recOpt
      )
      const smpOpt = 'realTime'

      // 저장 후 다시 불러올 ID를 담을 변수
      let targetId = purChasePlansData._id

      if (purChasePlansData._id) {
        // [UPDATE]
        await updateBuyTOU({
          variables: {
            val: {
              ...less,
              custId: custId,
              recOpt,
              smpOpt,
              recYear: recYearNum,
              smpYear: smpYearNum,
              holidayYear: holidayYearNum,
            },
          },
        })
      } else {
        // [CREATE]
        const { data: res } = await createBuyTOU({
          variables: {
            val: {
              ...less,
              custId: custId,
              recOpt,
              smpOpt,
              recYear: recYearNum,
              smpYear: smpYearNum,
              holidayYear: holidayYearNum,
            },
          },
        })

        // 신규 생성된 ID 확보
        if (res && res.createBuyTOU && res.createBuyTOU._id) {
          targetId = res.createBuyTOU._id
          setPurChasePlansData((prev) => ({
            ...prev,
            _id: targetId,
          }))
        }
      }

      // [핵심 수정] refetch() 대신 명시적으로 데이터를 다시 불러옵니다.
      // 이렇게 해야 신규 생성 시에도 그래프 데이터(touPeriodResult)를 확실하게 가져옵니다.
      if (targetId) {
        await findOnePurchasePlanData({ variables: { val: targetId } })
      }

      // 목록 갱신
      handleRefetch()

      // 화면 전환
      setIsCheck(true)
    } catch (e) {
      console.error(e)
      alert('저장 중 오류가 발생했습니다.')
      throw e
    }
  }

  const onDelete = async () => {
    if (purChasePlansData._id) {
      await deleteBuyTOU({
        variables: {
          val: purChasePlansData._id,
        },
      })

      handleResetAll()
      handleRefetch()
      setIsCheck(false)
    }
  }

  const onModify = async () => {
    setIsCheck(false)
  }

  const handleResetAll = () => {
    setPurChasePlansData(initializePurchasePlansData())
    loadedChargeUnitResult.current = undefined
    loadedCalculationKey.current = undefined
    setResetState(resetState === 0 ? 1 : 0)
    setSelectedPatternValue('realTime')
    setSelectedMonths([1])
    setPVData(undefined)
    setPvGraph([])
    setHolidaysData(undefined)
    setRealSmpData(undefined)
    setExtraSmpData(undefined)
    setRecData(undefined)
    setKepcoData(undefined)
    setUsingPvData([])
    setPvGraph([])
    setUseForCostSmpData([])
    setSeasonalRecAveragePrice({
      spr: 0,
      sum: 0,
      fall: 0,
      win: 0,
    })
    setSeasonalPVSum({
      spr: 0,
      sum: 0,
      fall: 0,
      win: 0,
    })
    setTouType(undefined)
    setSmpRate(INIT_TOU_RATE)
    setSmpTimePeriod(undefined)
    setKepcoTouRate(INIT_TOU_RATE)
    setPrevProfit({
      spr: 0,
      sum: 0,
      fall: 0,
      win: 0,
    })
    setPrevPlusAddProfit({
      spr: 0,
      sum: 0,
      fall: 0,
      win: 0,
    })
    setTouRate(INIT_TOU_RATE)
    setRecExpectedProfit({
      spr: 0,
      sum: 0,
      fall: 0,
      win: 0,
    })
  }

  const handleResetState = () => {
    setResetState(resetState === 0 ? 1 : 0)
  }

  return (
    <>
      <PageWrapper>
        <SearchListWrapper>
          <SearchList
            title="요금제 목록"
            onReset={handleResetAll}
            findOne={findOnePurchasePlanData}
            setIsCheck={setIsCheck}
            onEvent={handleResetState}
            findAllListgql={findAllBuyTOUGQL}
            setRefetch={setListRefetch}
            nameKey="name"
            descriptionRenderer={(item: any) => {
              const customer = PVCusList?.find(
                (cust: any) => cust._id === item.custId
              )
              return customer?.name || '고객 미지정'
            }}
          />
        </SearchListWrapper>
        {isCheck ? (
          <ContentWrapper>
            <Header>
              <TitleButton
                title={`${purChasePlansData.cust.name} - ${purChasePlansData.name} `}
                onDeleteClick={onDelete}
                onModify={onModify}
                isModify={isModify}
                isCheck={isCheck}
              />
            </Header>
            <BodyWrapper>
              <Body>
                <Left>
                  {purChasePlansData.planType !== 'flat' ? (
                    <DetailFrame>
                      <LabelOnly label="시간대 그래프" />
                      <GraphFrame>
                        <GraphWrapper>
                          <GraphBody>
                            <TOUHeatMap
                              width="100%"
                              height="130px"
                              title="봄"
                              data={purChasePlansData.touPeriodResult?.spr}
                            />
                            <TOUHeatMap
                              width="100%"
                              height="130px"
                              title="여름"
                              data={purChasePlansData.touPeriodResult?.sum}
                            />
                            <TOUHeatMap
                              width="100%"
                              height="130px"
                              title="가을"
                              data={purChasePlansData.touPeriodResult?.fall}
                            />
                            <TOUHeatMap
                              width="100%"
                              height="130px"
                              title="겨울"
                              data={purChasePlansData.touPeriodResult?.win}
                              showLegend
                            />
                          </GraphBody>
                        </GraphWrapper>
                      </GraphFrame>
                    </DetailFrame>
                  ) : null}
                  <LabelTable>
                    <TableWrapper>
                      <Table>
                        <TableRow>
                          <TableCell
                            style={{ borderTopLeftRadius: '4px' }}
                          ></TableCell>
                          <TableCell>봄</TableCell>
                          <TableCell>여름</TableCell>
                          <TableCell>가을</TableCell>
                          <TableCell style={{ borderTopRightRadius: '4px' }}>
                            겨울
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>경부하</TableCell>
                          <TableCell white>
                            {purChasePlansData.chargeUnitResult?.spr?.min}
                          </TableCell>
                          <TableCell white>
                            {purChasePlansData.chargeUnitResult?.sum?.min}
                          </TableCell>
                          <TableCell white>
                            {purChasePlansData.chargeUnitResult?.fall?.min}
                          </TableCell>
                          <TableCell white>
                            {purChasePlansData.chargeUnitResult?.win?.min}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>중간부하</TableCell>
                          <TableCell white>
                            {purChasePlansData.chargeUnitResult?.spr?.mid}
                          </TableCell>
                          <TableCell white>
                            {purChasePlansData.chargeUnitResult?.sum?.mid}
                          </TableCell>
                          <TableCell white>
                            {purChasePlansData.chargeUnitResult?.fall?.mid}
                          </TableCell>
                          <TableCell white>
                            {purChasePlansData.chargeUnitResult?.win?.mid}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>최대부하</TableCell>
                          <TableCell white>
                            {purChasePlansData.chargeUnitResult?.spr?.max}
                          </TableCell>
                          <TableCell white>
                            {purChasePlansData.chargeUnitResult?.sum?.max}
                          </TableCell>
                          <TableCell white>
                            {purChasePlansData.chargeUnitResult?.fall?.max}
                          </TableCell>
                          <TableCell
                            white
                            style={{ borderBottomRightRadius: '4px' }}
                          >
                            {purChasePlansData.chargeUnitResult?.win?.max}
                          </TableCell>
                        </TableRow>
                      </Table>
                    </TableWrapper>
                  </LabelTable>
                </Left>
                <Right>
                  <DetailFrame>
                    <LabelOnly label="선택한 옵션 목록" />
                    <OptionListWrapper>
                      <OptionDetailWrapper>
                        <OptionDetailBox>
                          <Rectangle />
                          <OptionTextBox>PV 계약 유형 : </OptionTextBox>
                          <OptionTextBox>
                            {getRevenueContractLabel(
                              selectedRevenueContractType
                            )}
                          </OptionTextBox>
                        </OptionDetailBox>
                        <OptionDetailBox>
                          <Rectangle />
                          <OptionTextBox>SMP 데이터 : </OptionTextBox>
                          <OptionTextBox>
                            {purChasePlansData.smpYear}
                          </OptionTextBox>
                        </OptionDetailBox>
                        <OptionDetailBox>
                          <Rectangle />
                          <OptionTextBox>한전 요금제 : </OptionTextBox>
                          <OptionTextBox>
                            {purChasePlansData.touVer}
                          </OptionTextBox>
                        </OptionDetailBox>
                        <OptionDetailBox>
                          <Rectangle />
                          <OptionTextBox>현물 REC : </OptionTextBox>
                          <OptionTextBox>
                            {purChasePlansData.recYear}
                          </OptionTextBox>
                        </OptionDetailBox>
                        <OptionDetailBox>
                          <Rectangle />
                          <OptionTextBox>단가계약방식 : </OptionTextBox>
                          <OptionTextBox>
                            {purChasePlansData.recOpt === 'energyAndRec'
                              ? '전력량과 REC 고정가격계약'
                              : '전력량만 구매'}
                          </OptionTextBox>
                        </OptionDetailBox>
                        {purChasePlansData.recOpt === 'energyAndRec' ? (
                          <OptionDetailBox>
                            <Rectangle />
                            <OptionTextBox>REC 지역 : </OptionTextBox>
                            <OptionTextBox>
                              {purChasePlansData.recRegion === 'jeju'
                                ? '제주'
                                : '육지'}
                            </OptionTextBox>
                          </OptionDetailBox>
                        ) : null}
                        <OptionDetailBox>
                          <Rectangle />
                          <OptionTextBox>공휴일 정보 : </OptionTextBox>
                          <OptionTextBox>
                            {purChasePlansData.holidayYear}
                          </OptionTextBox>
                        </OptionDetailBox>
                      </OptionDetailWrapper>
                      {/* 구분선*/}
                      <OptionDetailWrapper>
                        <OptionDetailBox>
                          <Rectangle />
                          <OptionTextBox>요금제 산정 방식 : </OptionTextBox>
                          <OptionTextBox>
                            {purChasePlansData.planType === 'flat'
                              ? '단일단가 요금제'
                              : 'TOU 요금제'}
                          </OptionTextBox>
                        </OptionDetailBox>
                      </OptionDetailWrapper>
                      <OptionDetailWrapper>
                        {/* SMP 선택 옵션은 항상 실시간 SMP로 고정되어 화면에서 숨김 처리합니다. */}
                        <OptionDetailBox>
                          <Rectangle />
                          <OptionTextBox>TOU 시간대 : </OptionTextBox>
                          <OptionTextBox>
                            {purChasePlansData.planType === 'flat'
                              ? '모든 계절 단일 시간대'
                              : purChasePlansData.timeTableOpt === 'kepco'
                                ? '한전 TOU 시간대'
                                : purChasePlansData.timeTableOpt === 'smp'
                                  ? 'SMP '
                                  : '단일 시간대'}
                          </OptionTextBox>
                        </OptionDetailBox>
                        <OptionDetailBox>
                          <Rectangle />
                          <OptionTextBox>가격비율 : </OptionTextBox>
                          <OptionTextBox>
                            {purChasePlansData.planType === 'flat'
                              ? '모든 계절 단일 단가'
                              : purChasePlansData.rateOpt === 'kepco'
                                ? '한전 TOU 시간대'
                                : purChasePlansData.rateOpt === 'smp'
                                  ? 'SMP '
                                  : purChasePlansData.rateOpt === 'single'
                                    ? '단일 단가'
                                    : '직접 입력'}
                          </OptionTextBox>
                        </OptionDetailBox>
                      </OptionDetailWrapper>
                    </OptionListWrapper>
                  </DetailFrame>
                </Right>
              </Body>
            </BodyWrapper>
          </ContentWrapper>
        ) : (
          <ContentWrapper>
            <Header>
              <TitleButton
                title="전력 구매 요금제 설계"
                onSaveClick={onSave}
                onCancleClick={handleResetAll}
                onDeleteClick={onDelete}
                isModify={isModify}
              />
              {/* TODO:예상수익 파라마티워 연결 필요*/}
              <PowerPurchaseBoard
                name={purChasePlansData.cust?.name}
                ratePlan={purChasePlansData.name}
                chartUnitResult={purChasePlansData.chargeUnitResult}
                beforeIncome={prevProfit}
                afterIncome={prevPlusAddProfit}
                recIncome={
                  purChasePlansData.recOpt === 'energyOnly'
                    ? recExpectedProfit
                    : undefined
                }
              />
            </Header>
            <BodyWrapper>
              <Body>
                <Left>
                  <SectionFrame>
                    <SectionHeader>
                      <SectionMark />
                      <SectionTitle>PV 발전사업자 선택</SectionTitle>
                      <SectionDivider />
                    </SectionHeader>
                    <LabelInput
                      label="PV 발전사업자 구매요금제명"
                      onChange={(e) => handleDataChange('name', e.target.value)}
                      value={purChasePlansData.name}
                    />
                    <DetailFrame>
                      <LabelAutoComplete
                        label="PV 발전사업자"
                        options={PVCusList}
                        value={purChasePlansData.cust}
                        setData={setPurChasePlansData}
                        fieldName="cust"
                      />
                      {selectedPvCustomer && (
                        <PvCustomerSummaryBox>
                          <PvCustomerSummaryItem>
                            <PvCustomerSummaryLabel>
                              설비 용량
                            </PvCustomerSummaryLabel>
                            <PvCustomerSummaryValue>
                              {formatSelectedPvNumber(
                                selectedPvCustomer.facilityCapacity,
                                ' kW'
                              )}
                            </PvCustomerSummaryValue>
                          </PvCustomerSummaryItem>
                          <PvCustomerSummaryItem>
                            <PvCustomerSummaryLabel>
                              REC 가중치
                            </PvCustomerSummaryLabel>
                            <PvCustomerSummaryValue>
                              {formatSelectedPvNumber(
                                selectedPvCustomer.RECWeight
                              )}
                            </PvCustomerSummaryValue>
                          </PvCustomerSummaryItem>
                          <PvCustomerSummaryItem>
                            <PvCustomerSummaryLabel>
                              기존 전력거래 방식
                            </PvCustomerSummaryLabel>
                            <PvCustomerSummaryValue>
                              {selectedPvSummaryContractLabel}
                              {selectedPvContractPriceText}
                            </PvCustomerSummaryValue>
                          </PvCustomerSummaryItem>
                        </PvCustomerSummaryBox>
                      )}
                    </DetailFrame>
                    <DetailFrame>
                      <LabelOnly label="PV 발전사업자 발전 패턴" />
                      <DetailColWrapper>
                        <DetailWrapper $direction="column">
                          <GraphWrapper height="auto" $width="100%">
                            <MonthChipWrapper>
                              {Array.from({ length: 12 }, (_, index) => (
                                <MonthChip
                                  key={index + 1}
                                  selected={selectedMonths.includes(index + 1)}
                                  onClick={() => toggleMonth(index + 1)}
                                >
                                  <MonthChipText>{`${index + 1}월`}</MonthChipText>
                                </MonthChip>
                              ))}
                            </MonthChipWrapper>
                            <GraphBody>
                              <MonthLineGraph2
                                month={selectedMonths[0]}
                                data={pvGraph ? pvGraph : []}
                                width="100%"
                                height="300px"
                                selectedChartDataType={selectedPatternValue}
                              />
                            </GraphBody>

                            <TextWrapper $align="center">
                              <Text>연간 발전량</Text>
                              <TextBox>
                                <Text fontColor="#57C9A7" fontSize="20px">
                                  총
                                </Text>
                                <TextBox2>
                                  <Text
                                    fontColor="#57C9A7"
                                    fontSize="22px"
                                    fontWeight="600"
                                  >
                                    {Number(
                                      (
                                        seasonalPVSum.spr +
                                        seasonalPVSum.sum +
                                        seasonalPVSum.fall +
                                        seasonalPVSum.win
                                      ).toFixed(0)
                                    ).toLocaleString()}
                                  </Text>
                                  <Text fontColor="#57C9A7" fontSize="18px">
                                    kWh
                                  </Text>
                                </TextBox2>
                              </TextBox>
                            </TextWrapper>

                            <SeasonFrame>
                              <SeasonBox>
                                <Text fontColor="#666" fontWeight="500">
                                  봄
                                </Text>
                                <SeasonTextBox>
                                  <Text
                                    fontColor="#4D4D4D"
                                    fontSize="15px"
                                    fontWeight="500"
                                  >
                                    {Number(
                                      seasonalPVSum.spr.toFixed(0)
                                    ).toLocaleString()}
                                  </Text>
                                  <Text fontColor="#666" fontWeight="500">
                                    kWh
                                  </Text>
                                </SeasonTextBox>
                              </SeasonBox>
                              <SeasonBox>
                                <Text fontColor="#666" fontWeight="500">
                                  여름
                                </Text>
                                <SeasonTextBox>
                                  <Text
                                    fontColor="#4D4D4D"
                                    fontSize="15px"
                                    fontWeight="500"
                                  >
                                    {Number(
                                      seasonalPVSum.sum.toFixed(0)
                                    ).toLocaleString()}
                                  </Text>
                                  <Text fontColor="#666" fontWeight="500">
                                    kWh
                                  </Text>
                                </SeasonTextBox>
                              </SeasonBox>
                              <SeasonBox>
                                <Text fontColor="#666" fontWeight="500">
                                  가을
                                </Text>
                                <SeasonTextBox>
                                  <Text
                                    fontColor="#4D4D4D"
                                    fontSize="15px"
                                    fontWeight="500"
                                  >
                                    {Number(
                                      seasonalPVSum.fall.toFixed(0)
                                    ).toLocaleString()}
                                  </Text>
                                  <Text fontColor="#666" fontWeight="500">
                                    kWh
                                  </Text>
                                </SeasonTextBox>
                              </SeasonBox>
                              <SeasonBox>
                                <Text fontColor="#666" fontWeight="500">
                                  겨울
                                </Text>
                                <SeasonTextBox>
                                  <Text
                                    fontColor="#4D4D4D"
                                    fontSize="15px"
                                    fontWeight="500"
                                  >
                                    {Number(
                                      seasonalPVSum.win.toFixed(0)
                                    ).toLocaleString()}
                                  </Text>
                                  <Text fontColor="#666" fontWeight="500">
                                    kWh
                                  </Text>
                                </SeasonTextBox>
                              </SeasonBox>
                            </SeasonFrame>
                          </GraphWrapper>
                        </DetailWrapper>
                      </DetailColWrapper>
                    </DetailFrame>
                  </SectionFrame>

                  <SectionFrame>
                    <SectionHeader>
                      <SectionMark />
                      <SectionTitle>설계 조건</SectionTitle>
                      <SectionDivider />
                    </SectionHeader>
                    <DetailFrame>
                      <DetailColWrapper>
                        <PvCustomerSummaryBox>
                          <PvCustomerSummaryItem>
                            <PvCustomerSummaryLabel>
                              전력시장 수수료 단가
                            </PvCustomerSummaryLabel>
                            <PvCustomerSummaryValue>
                              {formatSelectedPvNumber(
                                purChasePlansData.powerMarketTransactionUnit,
                                ' 원/kWh'
                              )}
                            </PvCustomerSummaryValue>
                          </PvCustomerSummaryItem>
                          <PvCustomerSummaryItem>
                            <PvCustomerSummaryLabel>
                              한전 TOU 요금제
                            </PvCustomerSummaryLabel>
                            <PvCustomerSummaryValue>
                              {formatDefaultValue(purChasePlansData.touVer)}
                            </PvCustomerSummaryValue>
                          </PvCustomerSummaryItem>
                          <PvCustomerSummaryItem>
                            <PvCustomerSummaryLabel>
                              현물 REC
                            </PvCustomerSummaryLabel>
                            <PvCustomerSummaryValue>
                              {formatDefaultValue(purChasePlansData.recYear)}
                            </PvCustomerSummaryValue>
                          </PvCustomerSummaryItem>
                          <PvCustomerSummaryItem>
                            <PvCustomerSummaryLabel>SMP</PvCustomerSummaryLabel>
                            <PvCustomerSummaryValue>
                              {formatDefaultValue(purChasePlansData.smpYear)}
                            </PvCustomerSummaryValue>
                          </PvCustomerSummaryItem>
                          <PvCustomerSummaryItem>
                            <PvCustomerSummaryLabel>
                              공휴일
                            </PvCustomerSummaryLabel>
                            <PvCustomerSummaryValue>
                              {formatDefaultValue(
                                purChasePlansData.holidayYear
                              )}
                            </PvCustomerSummaryValue>
                          </PvCustomerSummaryItem>
                        </PvCustomerSummaryBox>
                      </DetailColWrapper>
                    </DetailFrame>
                  </SectionFrame>

                  <SectionFrame>
                    <SectionHeader>
                      <SectionMark />
                      <SectionTitle>구매 계약 조건</SectionTitle>
                      <SectionDivider />
                    </SectionHeader>
                    <DetailFrame>
                      <LabelOnly label="단가계약방식" />
                      <DetailColWrapper>
                        <DetailWrapper style={{ flexDirection: 'column' }}>
                          <div
                            style={{
                              display: 'flex',
                              gap: '16px',
                              alignItems: 'flex-start',
                            }}
                          >
                            <ColumnRadioButton
                              list={[
                                {
                                  label: '전력량과 REC 고정가격계약',
                                },
                                {
                                  label: '전력량만 구매',
                                },
                              ]}
                              onChange={handleRecOptRadiobutton}
                              value={recOptRadio}
                            />
                            {purChasePlansData.recOpt === 'energyAndRec' ? (
                              <ColumnRadioButton
                                list={[
                                  {
                                    label: '육지',
                                  },
                                  {
                                    label: '제주',
                                  },
                                ]}
                                onChange={handleRecRegionRadiobutton}
                                value={recRegionRadio}
                              />
                            ) : null}
                          </div>
                          {purChasePlansData.recOpt === 'energyAndRec' ? (
                            <div style={{ width: '100%' }}>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  color: '#666',
                                  fontFamily: 'SUIT',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  lineHeight: '15px',
                                  marginBottom: '4px',
                                }}
                              >
                                단위: 원/MWh
                              </div>
                              <Table>
                                <TableRow>
                                  <TableCell2
                                    style={{ borderTopLeftRadius: '4px' }}
                                  ></TableCell2>
                                  <TableCell2>봄</TableCell2>
                                  <TableCell2>여름</TableCell2>
                                  <TableCell2>가을</TableCell2>
                                  <TableCell2
                                    style={{ borderTopRightRadius: '4px' }}
                                  >
                                    겨울
                                  </TableCell2>
                                </TableRow>
                                <TableRow>
                                  <TableCell2
                                    style={{ borderBottomLeftRadius: '4px' }}
                                  >
                                    REC 평균 가격
                                  </TableCell2>
                                  <TableCell2 white>
                                    {purChasePlansData.recYear &&
                                    seasonalRecAveragePrice.spr > 0
                                      ? seasonalRecAveragePrice.spr.toLocaleString()
                                      : '-'}
                                  </TableCell2>
                                  <TableCell2 white>
                                    {purChasePlansData.recYear &&
                                    seasonalRecAveragePrice.sum > 0
                                      ? seasonalRecAveragePrice.sum.toLocaleString()
                                      : '-'}
                                  </TableCell2>
                                  <TableCell2 white>
                                    {purChasePlansData.recYear &&
                                    seasonalRecAveragePrice.fall > 0
                                      ? seasonalRecAveragePrice.fall.toLocaleString()
                                      : '-'}
                                  </TableCell2>
                                  <TableCell2
                                    white
                                    style={{ borderBottomRightRadius: '4px' }}
                                  >
                                    {purChasePlansData.recYear &&
                                    seasonalRecAveragePrice.win > 0
                                      ? seasonalRecAveragePrice.win.toLocaleString()
                                      : '-'}
                                  </TableCell2>
                                </TableRow>
                              </Table>
                            </div>
                          ) : null}
                        </DetailWrapper>
                      </DetailColWrapper>
                    </DetailFrame>

                    {/* PV 발전사업자의 기존 수익 계산에 사용될 SMP 옵션 화면은 숨김 처리합니다. */}
                  </SectionFrame>
                </Left>
                <Right>
                  <SectionFrame>
                    <SectionHeader>
                      <SectionMark />
                      <SectionTitle>구매요금제 산정 방식</SectionTitle>
                      <SectionDivider />
                    </SectionHeader>
                    <DetailFrame>
                      <LabelOnly label="요금제 산정 방식" />
                      <DetailColWrapper>
                        <DetailWrapper>
                          <ColumnRadioButton
                            list={[
                              {
                                label: '모든 계절 단일단가 요금제',
                              },
                              {
                                label: 'TOU 요금제',
                              },
                            ]}
                            onChange={handlePlanTypeOpt}
                            value={planTypeRadio}
                          />
                        </DetailWrapper>
                      </DetailColWrapper>
                    </DetailFrame>
                  </SectionFrame>
                  {purChasePlansData.planType === 'tou' ? (
                    <SectionFrame>
                      <SectionHeader>
                        <SectionMark />
                        <CollapsibleSectionTitle
                          type="button"
                          aria-expanded={!isTouRateSectionCollapsed}
                          onClick={() =>
                            setIsTouRateSectionCollapsed((prev) => !prev)
                          }
                        >
                          TOU 시간대 구분 및 단가 산정 기준
                          <SectionTitleToggleText>
                            {isTouRateSectionCollapsed ? '열기' : '접기'}
                          </SectionTitleToggleText>
                          <SectionTitleChevron
                            $collapsed={isTouRateSectionCollapsed}
                          />
                        </CollapsibleSectionTitle>
                        <SectionDivider />
                      </SectionHeader>
                      {!isTouRateSectionCollapsed ? (
                        <>
                          <DetailFrame>
                            <LabelOnly label="계절별·시간대별 부하 구분 선택" />
                            <DetailColWrapper>
                              <DetailWrapper $direction="column">
                                <ColumnRadioButton
                                  list={[
                                    {
                                      label: '한전 TOU 기준',
                                    },
                                    {
                                      label: 'SMP 산정 기준',
                                    },
                                    {
                                      label: '단일 시간대 기준',
                                    },
                                  ]}
                                  onChange={handleTimeTableOpt}
                                  value={timeTableOptRadio}
                                />
                                <GraphWrapper $width="100%">
                                  <GraphBody>
                                    <TOUHeatMap
                                      width="100%"
                                      height="130px"
                                      title="봄"
                                      data={touType?.spr || {}}
                                    />
                                    <TOUHeatMap
                                      width="100%"
                                      height="130px"
                                      title="여름"
                                      data={touType?.sum}
                                    />
                                    <TOUHeatMap
                                      width="100%"
                                      height="130px"
                                      title="가을"
                                      data={touType?.fall}
                                    />
                                    <TOUHeatMap
                                      width="100%"
                                      height="130px"
                                      title="겨울"
                                      data={touType?.win}
                                      showLegend
                                    />
                                  </GraphBody>
                                </GraphWrapper>
                              </DetailWrapper>
                            </DetailColWrapper>
                          </DetailFrame>
                          <DetailFrame>
                            <LabelOnly label="최저부하 기준 중간·최대부하 단가 산정" />
                            <DetailColWrapper>
                              <DetailWrapper>
                                <div style={{ width: '65%' }}>
                                  <ColumnRadioCostRatio
                                    list={[
                                      {
                                        label: '한전 TOU 기준',
                                      },
                                      {
                                        label: 'SMP 가격 기준',
                                      },
                                      {
                                        label: '모든 시간대 동일 단가',
                                      },
                                      {
                                        label: '직접 입력',
                                      },
                                    ]}
                                    onChange={handleRateOpt}
                                    value={rateOptRadio}
                                  />
                                </div>
                                {rateOptRadio === '3' ? (
                                  <Table>
                                    <TableRow>
                                      <TableCell
                                        style={{ borderTopLeftRadius: '4px' }}
                                      ></TableCell>
                                      <TableCell>봄</TableCell>
                                      <TableCell>여름</TableCell>
                                      <TableCell>가을</TableCell>
                                      <TableCell
                                        style={{ borderTopRightRadius: '4px' }}
                                      >
                                        겨울
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell>중간부하 / 최저부하</TableCell>
                                      <TableCell white>
                                        <TableInput
                                          onChange={(e) =>
                                            handleDataChange(
                                              'sprMinMidRate',
                                              Number(e.target.value)
                                            )
                                          }
                                          type="number"
                                          value={
                                            purChasePlansData.sprMinMidRate
                                          }
                                        />
                                      </TableCell>
                                      <TableCell white>
                                        <TableInput
                                          onChange={(e) =>
                                            handleDataChange(
                                              'sumMinMidRate',
                                              Number(e.target.value)
                                            )
                                          }
                                          type="number"
                                          value={
                                            purChasePlansData.sumMinMidRate
                                          }
                                        />
                                      </TableCell>
                                      <TableCell white>
                                        <TableInput
                                          onChange={(e) =>
                                            handleDataChange(
                                              'fallMinMidRate',
                                              Number(e.target.value)
                                            )
                                          }
                                          type="number"
                                          value={
                                            purChasePlansData.fallMinMidRate
                                          }
                                        />
                                      </TableCell>
                                      <TableCell white>
                                        <TableInput
                                          onChange={(e) =>
                                            handleDataChange(
                                              'winMinMidRate',
                                              Number(e.target.value)
                                            )
                                          }
                                          type="number"
                                          value={
                                            purChasePlansData.winMinMidRate
                                          }
                                        />
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell
                                        style={{
                                          borderBottomLeftRadius: '4px',
                                        }}
                                      >
                                        최대부하 / 최저부하
                                      </TableCell>
                                      <TableCell white>
                                        <TableInput
                                          value={
                                            purChasePlansData.sprMinMaxRate
                                          }
                                          onChange={(e) =>
                                            handleDataChange(
                                              'sprMinMaxRate',
                                              Number(e.target.value)
                                            )
                                          }
                                          type="number"
                                        />
                                      </TableCell>
                                      <TableCell white>
                                        <TableInput
                                          onChange={(e) =>
                                            handleDataChange(
                                              'sumMinMaxRate',
                                              Number(e.target.value)
                                            )
                                          }
                                          type="number"
                                          value={
                                            purChasePlansData.sumMinMaxRate
                                          }
                                        />
                                      </TableCell>
                                      <TableCell white>
                                        <TableInput
                                          onChange={(e) =>
                                            handleDataChange(
                                              'fallMinMaxRate',
                                              Number(
                                                e.target.value.toLocaleString()
                                              )
                                            )
                                          }
                                          type="number"
                                          value={
                                            purChasePlansData.fallMinMaxRate
                                          }
                                        />
                                      </TableCell>
                                      <TableCell
                                        white
                                        style={{
                                          borderBottomRightRadius: '4px',
                                        }}
                                      >
                                        <TableInput
                                          onChange={(e) =>
                                            handleDataChange(
                                              'winMinMaxRate',
                                              Number(e.target.value)
                                            )
                                          }
                                          type="number"
                                          value={
                                            purChasePlansData.winMinMaxRate
                                          }
                                        />
                                      </TableCell>
                                    </TableRow>
                                  </Table>
                                ) : (
                                  <Table>
                                    <TableRow>
                                      <TableCell
                                        style={{ borderTopLeftRadius: '4px' }}
                                      ></TableCell>
                                      <TableCell>봄</TableCell>
                                      <TableCell>여름</TableCell>
                                      <TableCell>가을</TableCell>
                                      <TableCell
                                        style={{ borderTopRightRadius: '4px' }}
                                      >
                                        겨울
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell>중간부하 / 최저부하</TableCell>
                                      <TableCell white>
                                        {purChasePlansData.sprMinMidRate?.toLocaleString()}
                                      </TableCell>
                                      <TableCell white>
                                        {purChasePlansData.sumMinMidRate?.toLocaleString()}
                                      </TableCell>
                                      <TableCell white>
                                        {purChasePlansData.fallMinMidRate?.toLocaleString()}
                                      </TableCell>
                                      <TableCell white>
                                        {purChasePlansData.winMinMidRate?.toLocaleString()}
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell
                                        style={{
                                          borderBottomLeftRadius: '4px',
                                        }}
                                      >
                                        최대부하 / 최저부하
                                      </TableCell>
                                      <TableCell white>
                                        {purChasePlansData.sprMinMaxRate?.toLocaleString()}
                                      </TableCell>
                                      <TableCell white>
                                        {purChasePlansData.sumMinMaxRate?.toLocaleString()}
                                      </TableCell>
                                      <TableCell white>
                                        {purChasePlansData.fallMinMaxRate?.toLocaleString()}
                                      </TableCell>
                                      <TableCell
                                        white
                                        style={{
                                          borderBottomRightRadius: '4px',
                                        }}
                                      >
                                        {purChasePlansData.winMinMaxRate?.toLocaleString()}
                                      </TableCell>
                                    </TableRow>
                                  </Table>
                                )}
                              </DetailWrapper>
                            </DetailColWrapper>
                          </DetailFrame>
                        </>
                      ) : null}
                    </SectionFrame>
                  ) : (
                    <></>
                  )}

                  <SectionFrame>
                    <SectionHeader>
                      <SectionMark />
                      <SectionTitle>목표 수익 및 구매단가 확인</SectionTitle>
                      <SectionDivider />
                    </SectionHeader>
                    <DetailFrame>
                      <LabelUnit
                        label="PV 발전사업자 목표 수익 설정"
                        unit="단위: 원"
                      />
                      <DetailColWrapper>
                        <DetailWrapper>
                          {purChasePlansData.planType === 'flat' ? (
                            <TargetRevenueCard>
                              <TargetRevenueSummary>
                                <TargetRevenueSummaryLabel>
                                  PV 발전사업자 연간 기존 수익
                                </TargetRevenueSummaryLabel>
                                <TargetRevenueSummaryValue>
                                  {
                                    flatPrevProfit
                                      .toLocaleString()
                                      .split('.')[0]
                                  }
                                  원
                                </TargetRevenueSummaryValue>
                              </TargetRevenueSummary>

                              <TargetRevenueFieldGrid>
                                <TargetRevenueField>
                                  <TargetRevenueFieldLabel>
                                    PV 발전사업자
                                    <br />
                                    연간 수익 증감액
                                  </TargetRevenueFieldLabel>
                                  <TargetRevenueInputRow>
                                    <TargetRevenueInputBox>
                                      <TableInput
                                        onChange={(e: any) =>
                                          handleSeasonCostInputChange(
                                            e,
                                            'flatAddEarnings',
                                            Number(e.target.value)
                                          )
                                        }
                                        type="number"
                                        value={
                                          purChasePlansData.flatAddEarnings
                                        }
                                        height="38px"
                                      />
                                    </TargetRevenueInputBox>
                                    <TargetRevenueUnit>원</TargetRevenueUnit>
                                  </TargetRevenueInputRow>
                                  <TargetRevenueFieldHelp>
                                    기존 연간 수익에 더하거나 차감할 금액입니다.
                                  </TargetRevenueFieldHelp>
                                  <TargetRevenueDeltaPreview
                                    $tone={flatEarningsTone}
                                  >
                                    {purChasePlansData.flatAddEarnings > 0
                                      ? '+'
                                      : ''}
                                    {purChasePlansData.flatAddEarnings.toLocaleString()}
                                    원
                                  </TargetRevenueDeltaPreview>
                                </TargetRevenueField>

                                <TargetRevenueField>
                                  <TargetRevenueFieldLabel $minHeight="32px">
                                    전력 구매 목표단가
                                  </TargetRevenueFieldLabel>
                                  <TargetRevenueInputRow>
                                    <TargetRevenueInputBox>
                                      <TableInput
                                        onChange={
                                          handleFlatTargetUnitInputChange
                                        }
                                        type="number"
                                        value={flatTargetUnit}
                                        height="38px"
                                      />
                                    </TargetRevenueInputBox>
                                    <TargetRevenueUnit>
                                      원/kWh
                                    </TargetRevenueUnit>
                                  </TargetRevenueInputRow>
                                  <TargetRevenueFieldHelp>
                                    목표단가를 입력하면 연간 증감액이 자동
                                    계산됩니다.
                                  </TargetRevenueFieldHelp>
                                </TargetRevenueField>
                              </TargetRevenueFieldGrid>

                              <TargetRevenueResult>
                                <TargetRevenueResultLabel>
                                  PV 발전사업자 연간 예상 수익
                                </TargetRevenueResultLabel>
                                <TargetRevenueResultValue>
                                  {
                                    flatTargetProfit
                                      .toLocaleString()
                                      .split('.')[0]
                                  }
                                  원
                                </TargetRevenueResultValue>
                              </TargetRevenueResult>
                            </TargetRevenueCard>
                          ) : (
                            <TargetRevenueCard>
                              <TargetRevenueSummary>
                                <TargetRevenueSummaryLabel>
                                  PV 발전사업자 연간 기존 수익
                                </TargetRevenueSummaryLabel>
                                <TargetRevenueSummaryValue>
                                  {
                                    flatPrevProfit
                                      .toLocaleString()
                                      .split('.')[0]
                                  }
                                  원
                                </TargetRevenueSummaryValue>
                              </TargetRevenueSummary>

                              <TargetRevenueField>
                                <TargetRevenueFieldLabel>
                                  PV 발전사업자
                                  <br />
                                  연간 수익 증감액
                                </TargetRevenueFieldLabel>
                                <TargetRevenueInputRow>
                                  <TargetRevenueInputBox>
                                    <TableInput
                                      onChange={
                                        handleTouAnnualEarningsInputChange
                                      }
                                      value={total}
                                      type="number"
                                      height="38px"
                                    />
                                  </TargetRevenueInputBox>
                                  <TargetRevenueUnit>원</TargetRevenueUnit>
                                </TargetRevenueInputRow>
                                <TargetRevenueFieldHelp>
                                  입력한 금액은 봄, 여름, 가을에 동일하게
                                  배분하고 남은 금액은 겨울에 반영됩니다.
                                </TargetRevenueFieldHelp>
                              </TargetRevenueField>

                              <TargetRevenueToggleButton
                                type="button"
                                onClick={() =>
                                  setIsTouSeasonDetailOpen((prev) => !prev)
                                }
                              >
                                {isTouSeasonDetailOpen
                                  ? '계절별 직접 조정 접기'
                                  : '계절별 직접 조정'}
                              </TargetRevenueToggleButton>

                              <TargetSeasonGrid>
                                {touSeasonTargets.map((season) => {
                                  const seasonTargetProfit =
                                    season.prevProfit + season.addEarnings
                                  const seasonTone =
                                    season.addEarnings > 0
                                      ? 'positive'
                                      : season.addEarnings < 0
                                        ? 'negative'
                                        : 'neutral'

                                  return (
                                    <TargetSeasonCard key={season.label}>
                                      <TargetSeasonHeader>
                                        {season.label}
                                      </TargetSeasonHeader>
                                      <TargetSeasonMetric>
                                        <TargetSeasonMetricLabel>
                                          기존 수익
                                        </TargetSeasonMetricLabel>
                                        <TargetSeasonMetricValue>
                                          {
                                            season.prevProfit
                                              .toLocaleString()
                                              .split('.')[0]
                                          }
                                          원
                                        </TargetSeasonMetricValue>
                                      </TargetSeasonMetric>
                                      {isTouSeasonDetailOpen ? (
                                        <>
                                          <TargetRevenueFieldLabel>
                                            기존 수익 대비 증감액
                                          </TargetRevenueFieldLabel>
                                          <TargetRevenueInputRow>
                                            <TargetRevenueInputBox>
                                              <TableInput
                                                onChange={(e: any) =>
                                                  handleSeasonCostInputChange(
                                                    e,
                                                    season.addEarningsKey,
                                                    Number(e.target.value)
                                                  )
                                                }
                                                value={season.addEarnings}
                                                type="number"
                                                height="38px"
                                              />
                                            </TargetRevenueInputBox>
                                            <TargetRevenueUnit>
                                              원
                                            </TargetRevenueUnit>
                                          </TargetRevenueInputRow>
                                        </>
                                      ) : (
                                        <TargetSeasonMetric>
                                          <TargetSeasonMetricLabel>
                                            배분 증감액
                                          </TargetSeasonMetricLabel>
                                          <TargetRevenueDeltaPreview
                                            $tone={seasonTone}
                                          >
                                            {season.addEarnings > 0 ? '+' : ''}
                                            {season.addEarnings.toLocaleString()}
                                            원
                                          </TargetRevenueDeltaPreview>
                                        </TargetSeasonMetric>
                                      )}
                                      <TargetSeasonMetric>
                                        <TargetSeasonMetricLabel>
                                          목표 수익
                                        </TargetSeasonMetricLabel>
                                        <TargetSeasonMetricValue>
                                          {
                                            seasonTargetProfit
                                              .toLocaleString()
                                              .split('.')[0]
                                          }
                                          원
                                        </TargetSeasonMetricValue>
                                      </TargetSeasonMetric>
                                    </TargetSeasonCard>
                                  )
                                })}
                              </TargetSeasonGrid>

                              <TargetRevenueResult>
                                <TargetRevenueResultLabel>
                                  PV 발전사업자 연간 수익 증감액
                                </TargetRevenueResultLabel>
                                <TargetRevenueDeltaPreview
                                  $tone={touEarningsTone}
                                >
                                  {total > 0 ? '+' : ''}
                                  {total.toLocaleString()}원
                                </TargetRevenueDeltaPreview>
                                <TargetRevenueResultLabel>
                                  PV 발전사업자 연간 예상 수익
                                </TargetRevenueResultLabel>
                                <TargetRevenueResultValue>
                                  {
                                    touTargetProfit
                                      .toLocaleString()
                                      .split('.')[0]
                                  }
                                  원
                                </TargetRevenueResultValue>
                              </TargetRevenueResult>
                            </TargetRevenueCard>
                          )}
                        </DetailWrapper>
                      </DetailColWrapper>
                    </DetailFrame>
                  </SectionFrame>
                </Right>
              </Body>
            </BodyWrapper>
          </ContentWrapper>
        )}
      </PageWrapper>
    </>
  )
}
